import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { X, CurrencyDollar, Coins } from "@phosphor-icons/react";
import { toast } from "sonner";

/** Modal for admin to issue a one-time custom charge to a client.
 *  Required: client (with id, name, email). Optional: projectId (links charge to a project).
 *  Optional: defaultDescription. Calls onCreated with new charge. */
export function CustomChargeModal({ open, onClose, client, projectId, defaultDescription = "", onCreated }) {
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState(defaultDescription);
  const [applyToProject, setApplyToProject] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!open || !client) return null;

  const submit = async () => {
    const num = parseFloat(amount);
    if (!num || num <= 0) {
      toast.error("Enter a positive amount");
      return;
    }
    if (!description.trim() || description.trim().length < 3) {
      toast.error("Add a short description");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/custom-charges", {
        client_id: client.id,
        amount: num,
        description: description.trim(),
        project_id: projectId || null,
        apply_to_project_balance: !!(projectId && applyToProject),
      });
      toast.success(`Charge of $${num.toFixed(2)} sent to ${client.name}`);
      setAmount("");
      setDescription("");
      setApplyToProject(false);
      onCreated?.(data);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to issue charge");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="custom-charge-modal">
      <div className="border border-[#FF2D2D] bg-[#0a0a0a] w-full max-w-md p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Custom charge</div>
            <h3 className="font-display text-2xl mt-1">Send a payment request</h3>
            <div className="text-xs text-[#a3a3a3] mt-1 truncate">
              To: <span className="text-white">{client.name}</span> · {client.email}
            </div>
          </div>
          <button onClick={onClose} className="text-[#a3a3a3] hover:text-white"><X size={18} /></button>
        </div>

        <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">Amount (USD)</label>
        <div className="relative">
          <CurrencyDollar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#FF2D2D]" weight="fill" />
          <input
            type="number"
            min="1"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            data-testid="charge-amount-input"
            placeholder="125.00"
            className="w-full bg-[#050505] border border-[#262626] pl-9 pr-4 py-3 text-lg focus:border-[#FF2D2D] outline-none tabular-nums"
          />
        </div>

        <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mt-4 mb-2">Reason for charge</label>
        <textarea
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          data-testid="charge-description-input"
          placeholder="e.g. Extra 3 pages added to website · Premium plugin license"
          className="w-full bg-[#050505] border border-[#262626] px-4 py-3 text-sm focus:border-[#FF2D2D] outline-none"
        />
        <div className="text-xs text-[#a3a3a3] mt-2">
          They'll receive a Stripe payment link via email and on their dashboard.
        </div>

        {projectId && (
          <label className={`mt-4 flex items-start gap-3 p-3 border cursor-pointer ${applyToProject ? "border-[#FF2D2D] bg-[#FF2D2D]/5" : "border-[#262626]"}`}>
            <input
              type="checkbox"
              checked={applyToProject}
              onChange={(e) => setApplyToProject(e.target.checked)}
              data-testid="charge-apply-to-project"
              className="mt-1"
            />
            <div className="flex-1">
              <div className="text-sm font-semibold flex items-center gap-1.5">
                <Coins size={12} weight={applyToProject ? "fill" : "regular"} className={applyToProject ? "text-[#FF2D2D]" : ""} />
                Apply to project balance when paid
              </div>
              <div className="text-xs text-[#a3a3a3] mt-1">When the client pays, this amount auto-credits the project's <code>paid_amount</code>. Use for over-budget overages instead of separate line items.</div>
            </div>
          </label>
        )}

        <div className="flex gap-3 mt-6">
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="charge-submit"
            className="btn-primary flex-1 px-4 py-3 text-sm font-bold uppercase tracking-wider disabled:opacity-50"
          >
            {submitting ? "Sending…" : "Issue charge →"}
          </button>
          <button onClick={onClose} className="btn-outline flex-1 px-4 py-3 text-sm uppercase tracking-wider">Cancel</button>
        </div>
      </div>
    </div>
  );
}
